import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  subscribeChats, subscribeMessages, sendMessage, markChatRead,
  loadOlderMessages, openChat, editMessage, deleteMessage, hideChat,
  clearedCutoff, MESSAGE_PAGE,
} from '../firebase/chatService';

// ── CHAT HOOKS ───────────────────────────────────────────────────────────────
// The listeners live HERE, in exactly two places, and both return their
// unsubscribe from the effect. That is the whole point of putting them in
// hooks: a listener created inside a component body, or in an effect whose
// cleanup forgets to call it, keeps streaming — and keeps billing — after the
// component is gone. Every subscription in this file is torn down on unmount
// and re-created when its key changes.

/**
 * My conversation list, live.
 *
 * Bounded and denormalised at the service layer, so this is one query for the
 * whole list — no per-chat profile or message reads.
 */
export function useChats() {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) { setChats([]); setLoading(false); return undefined; }
    setLoading(true);
    const unsubscribe = subscribeChats(user.uid, (rows) => {
      setChats(rows);
      setLoading(false);
    });
    // REQUIRED. Without this the listener outlives the page.
    return unsubscribe;
  }, [user?.uid]);

  const totalUnread = chats.reduce(
    (sum, c) => sum + (c.unread?.[user?.uid] || 0), 0);

  // Removes a conversation from MY list only. The live listener drops the row
  // as soon as the write lands, so there is nothing to update locally.
  const hide = useCallback(async (chatId) => {
    if (!user?.uid) return;
    try { await hideChat(chatId, user.uid); }
    catch (err) { console.error('[chat] could not hide:', err); }
  }, [user?.uid]);

  return { chats, loading, totalUnread, hide };
}

/**
 * One thread, live, capped at the most recent `MESSAGE_PAGE` messages.
 *
 * `chatId` may be null (nothing selected) — the effect then subscribes to
 * nothing rather than the caller having to branch.
 */
export function useChat(chatId, otherUid) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [older, setOlder] = useState([]);      // pages fetched on demand
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [sending, setSending] = useState(false);

  // Guards a stale `markChatRead` from a thread that has since been closed.
  const activeChat = useRef(chatId);
  activeChat.current = chatId;

  // This user's deleted-history cutoff, once known — needed by loadOlder too.
  const cutoff = useRef(null);

  useEffect(() => {
    setOlder([]);
    setExhausted(false);
    cutoff.current = null;
    if (!chatId || !user?.uid) { setMessages([]); setLoading(false); return undefined; }

    setLoading(true);

    // The cutoff has to be known BEFORE the listener starts. Subscribing first
    // and filtering afterwards would paint the deleted history for a frame,
    // which rather defeats deleting it.
    let unsubscribe = () => {};
    let cancelled = false;

    clearedCutoff(chatId, user.uid).then((since) => {
      if (cancelled) return;
      cutoff.current = since;
      unsubscribe = subscribeMessages(chatId, (rows) => {
        setMessages(rows);
        setLoading(false);
      }, MESSAGE_PAGE, since);
    });

    // REQUIRED. Switching threads must close the previous listener, or an
    // afternoon of clicking through conversations leaves a dozen of them open.
    // `cancelled` covers the window before the listener exists: without it, a
    // thread closed during that await would subscribe after unmount and leak.
    return () => { cancelled = true; unsubscribe(); };
  }, [chatId, user?.uid]);

  // Seeing a thread is what marks it read — one small write, only when there is
  // actually something unread, so idling in an open chat costs nothing.
  useEffect(() => {
    if (!chatId || !user?.uid || !messages.length) return;
    const lastFromThem = messages[messages.length - 1]?.senderUid !== user.uid;
    if (lastFromThem) markChatRead(chatId, user.uid);
  }, [chatId, user?.uid, messages]);

  const send = useCallback(async (text, opts) => {
    if (!chatId || !user?.uid || !otherUid || sending) return;
    setSending(true);
    try {
      await sendMessage(chatId, user.uid, otherUid, text, opts);
    } finally {
      setSending(false);
    }
  }, [chatId, user?.uid, otherUid, sending]);

  const loadOlder = useCallback(async () => {
    const all = [...older, ...messages];
    if (!chatId || loadingOlder || exhausted || !all.length) return;
    setLoadingOlder(true);
    try {
      const page = await loadOlderMessages(chatId, all[0], MESSAGE_PAGE, cutoff.current);
      if (!page.length) setExhausted(true);
      else setOlder(prev => [...page, ...prev]);
    } finally {
      setLoadingOlder(false);
    }
  }, [chatId, older, messages, loadingOlder, exhausted]);

  const edit = useCallback(async (messageId, text) => {
    if (!chatId) return;
    try { await editMessage(chatId, messageId, text); }
    catch (err) { console.error('[chat] could not edit:', err); }
  }, [chatId]);

  const remove = useCallback(async (messageId) => {
    if (!chatId) return;
    // The preview quotes the newest message; if that is the one going away,
    // hand the parent whatever now sits at the end.
    const all = older.length ? [...older, ...messages] : messages;
    const wasNewest = all[all.length - 1]?.id === messageId;
    const nextNewest = wasNewest ? all[all.length - 2] : undefined;
    try {
      await deleteMessage(chatId, messageId, wasNewest ? (nextNewest || null) : undefined);
      setOlder(prev => prev.filter(m => m.id !== messageId));
    } catch (err) { console.error('[chat] could not delete:', err); }
  }, [chatId, older, messages]);

  return {
    // Older pages sit in front of the live window; ids are unique so the two
    // can never show the same message twice.
    messages: older.length ? [...older, ...messages] : messages,
    loading,
    sending,
    send,
    edit,
    remove,
    loadOlder,
    loadingOlder,
    canLoadOlder: !exhausted && messages.length >= MESSAGE_PAGE,
  };
}

/**
 * Opens (or creates) the conversation with someone, then hands back its id.
 *
 * Deliberately NOT a listener — it is one document get, and the thread's own
 * hook takes over from there.
 */
export function useOpenChat() {
  const { user, profile } = useAuth();
  const [busy, setBusy] = useState(false);

  const start = useCallback(async (them) => {
    if (!user?.uid || !them?.uid || user.uid === them.uid) return null;
    setBusy(true);
    try {
      const chat = await openChat(
        {
          uid: user.uid,
          name: profile?.fullName || user.displayName || '',
          avatar: profile?.avatarUrl || '',
        },
        them,
      );
      return chat.id;
    } catch (err) {
      console.error('[chat] could not open:', err);
      return null;
    } finally {
      setBusy(false);
    }
  }, [user?.uid, user?.displayName, profile?.fullName, profile?.avatarUrl]);

  return { start, busy };
}
